import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Modal,
  Select,
  Row,
  Col,
  Typography,
  Statistic,
  Rate,
  Upload,
  Spin,
  message,
  Progress,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadProps } from 'antd/es/upload';
import {
  PlusOutlined,
  ShopOutlined,
  PhoneOutlined,
  MailOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  StarFilled,
  DownloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '@/services/api.service';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { postExcelImportWithRetry } from '@/utils/excelImportUpload';
import { approveSupplier } from '@/services/supplierLifecycle.service';

const { Title, Text } = Typography;
const { Search } = Input;

const SUPPLIERS_QUERY_KEY = ['procurement-suppliers'];
const SUPPLIERS_STATS_KEY = ['procurement-suppliers-statistics'];

function unwrap<T>(res: unknown): T {
  const data = (res as any)?.data ?? res;
  return (data?.data ?? data) as T;
}

export interface SupplierApi {
  id: string;
  supplier_code: string;
  supplier_name: string;
  supplier_type: string;
  category?: string;
  supplier_category_ids?: string[] | null;
  contact_person?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  address_line1?: string;
  city?: string;
  country?: string;
  tax_registration_number?: string;
  payment_terms?: string;
  lead_time_days?: number;
  currency?: string;
  quality_rating?: number;
  delivery_rating?: number;
  service_rating?: number;
  is_active?: boolean;
  is_approved?: boolean;
  qualification_status?: string;
  supplier_segment?: string;
  enterprise_supplier_score?: number;
  created_at?: string;
}

const supplierTypeColors: { [key: string]: string } = {
  Material: 'blue',
  Service: 'green',
  Subcontractor: 'orange',
  Equipment: 'purple',
  Vendor: 'blue',
};

function avgRating(s: SupplierApi): number {
  const q = Number(s.quality_rating) || 0;
  const d = Number(s.delivery_rating) || 0;
  const sv = Number(s.service_rating) || 0;
  if (q === 0 && d === 0 && sv === 0) return 0;
  return Math.round(((q + d + sv) / 3) * 2) / 2;
}

export const SuppliersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const suppliersListPath = location.pathname.startsWith('/platform')
    ? '/platform/foundation/suppliers'
    : '/scm/procurement/suppliers';

  const openSupplier360 = (supplierId: string) => {
    navigate(`${suppliersListPath}/${supplierId}/360`);
  };
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedValue(searchText.trim(), 300);
  const [isExcelBusy, setIsExcelBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [supplierPage, setSupplierPage] = useState(1);
  const [supplierPageSize, setSupplierPageSize] = useState(20);

  const {
    data: suppliersRaw = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: [...SUPPLIERS_QUERY_KEY, debouncedSearch || ''],
    queryFn: async () => {
      const res = await api.get('/procurement/suppliers', {
        params: debouncedSearch ? { search: debouncedSearch } : undefined,
      });
      const list = unwrap<SupplierApi[]>(res);
      return Array.isArray(list) ? list : [];
    },
  });

  const { data: statistics } = useQuery({
    queryKey: SUPPLIERS_STATS_KEY,
    queryFn: async () => {
      const res = await api.get('/procurement/suppliers/statistics');
      return unwrap<{
        totalSuppliers: number;
        activeSuppliers: number;
        approvedSuppliers: number;
      }>(res);
    },
  });

  const { data: supplierCategories = [] } = useQuery({
    queryKey: ['procurement-supplier-categories'],
    queryFn: async () => {
      const res = await api.get('/procurement/supplier-categories');
      const rows = unwrap<{ id: string; category_name: string }[]>(res);
      return Array.isArray(rows) ? rows : [];
    },
  });

  const categoryIdToName = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of supplierCategories) {
      m.set(c.id, c.category_name);
    }
    return m;
  }, [supplierCategories]);

  const supplierCategoryLabels = useCallback(
    (s: SupplierApi): string[] => {
      const ids = (s.supplier_category_ids ?? []).filter(Boolean);
      if (ids.length > 0) {
        return ids.map((id) => categoryIdToName.get(id)).filter(Boolean) as string[];
      }
      if (s.category) return [s.category];
      return [];
    },
    [categoryIdToName]
  );

  useEffect(() => {
    setSupplierPage(1);
  }, [debouncedSearch, suppliersRaw]);

  const columns: ColumnsType<SupplierApi> = [
    {
      title: 'Code',
      dataIndex: 'supplier_code',
      key: 'supplier_code',
      width: 120,
      sorter: (a, b) => (a.supplier_code || '').localeCompare(b.supplier_code || ''),
    },
    {
      title: 'Supplier',
      key: 'details',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Space>
            <Text strong>{record.supplier_name}</Text>
            {avgRating(record) >= 4 && <StarFilled style={{ color: '#faad14' }} />}
          </Space>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.contact_person || '—'}
          </Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            <PhoneOutlined /> {record.phone || '—'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.city || '—'}</Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {record.country || '—'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'supplier_type',
      key: 'supplier_type',
      width: 120,
      render: (type: string) => (
        <Tag color={supplierTypeColors[type] || 'default'}>{type || '—'}</Tag>
      ),
    },
    {
      title: 'Categories',
      key: 'categories',
      width: 200,
      render: (_, record) => {
        const labels = supplierCategoryLabels(record);
        if (!labels.length) return <Text type="secondary">—</Text>;
        return (
          <Space size={[4, 4]} wrap>
            {labels.map((l) => (
              <Tag key={l}>{l}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Rating',
      key: 'rating',
      width: 120,
      render: (_, record) => <Rate disabled value={avgRating(record)} style={{ fontSize: '14px' }} />,
      sorter: (a, b) => avgRating(a) - avgRating(b),
    },
    {
      title: 'Lead time',
      dataIndex: 'lead_time_days',
      key: 'lead_time_days',
      width: 100,
      render: (days: number) => {
        const d = Number(days) || 0;
        const color = d <= 7 ? '#52c41a' : d <= 14 ? '#faad14' : '#f5222d';
        return <Text style={{ color }}>{d} days</Text>;
      },
    },
    {
      title: 'Qualification',
      key: 'qualification',
      width: 130,
      render: (_, record) => (
        <Tag color={record.qualification_status === 'Qualified' ? 'success' : 'default'}>
          {record.qualification_status ?? 'Pending'}
        </Tag>
      ),
    },
    {
      title: 'Segment',
      dataIndex: 'supplier_segment',
      key: 'supplier_segment',
      width: 110,
      render: (seg?: string) => (seg ? <Tag>{seg}</Tag> : <Text type="secondary">—</Text>),
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, record) =>
        record.is_active ? <Tag color="success">Active</Tag> : <Tag>Inactive</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openSupplier360(record.id)}>
            360°
          </Button>
          {!record.is_approved ? (
            <Button type="link" size="small" onClick={() => handleApprove(record)}>
              Approve
            </Button>
          ) : null}
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button
            type="text"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  const triggerBlobDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = async () => {
    setIsExcelBusy(true);
    try {
      const res = await api.getClient().get('/procurement/suppliers/excel/template', {
        responseType: 'blob',
      });
      triggerBlobDownload(
        new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        'suppliers-import-template.xlsx'
      );
      message.success('Template downloaded');
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Download failed');
    } finally {
      setIsExcelBusy(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExcelBusy(true);
    try {
      const res = await api.getClient().get('/procurement/suppliers/excel/export', {
        responseType: 'blob',
      });
      const dateStr = new Date().toISOString().slice(0, 10);
      triggerBlobDownload(
        new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        `suppliers-${dateStr}.xlsx`
      );
      message.success('Export downloaded');
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Export failed');
    } finally {
      setIsExcelBusy(false);
    }
  };

  const handleImportExcel: UploadProps['customRequest'] = async (options) => {
    const { file, onError, onSuccess } = options;
    const formData = new FormData();
    formData.append('file', file as File);
    setIsExcelBusy(true);
    setUploadPct(0);
    try {
      const res = await postExcelImportWithRetry(
        api.getClient(),
        '/procurement/suppliers/excel/import',
        formData,
        { onUploadProgress: (p) => setUploadPct(p) }
      );
      const body = res.data as {
        success?: boolean;
        message?: string;
        data?: { created: number; updated: number; errors?: string[] };
      };
      if (body?.success !== false) {
        const d = body?.data;
        const errList = d?.errors?.length ? ` (${d.errors.length} row message(s))` : '';
        message.success((body?.message || 'Import completed') + errList);
        if (d?.errors?.length) {
          Modal.warning({
            title: 'Import notes',
            width: 560,
            content: (
              <div style={{ maxHeight: 280, overflow: 'auto' }}>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {d.errors!.slice(0, 50).map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
                {d.errors!.length > 50 ? <Text type="secondary">…and more</Text> : null}
              </div>
            ),
          });
        }
        await queryClient.invalidateQueries({ queryKey: SUPPLIERS_QUERY_KEY });
        await queryClient.invalidateQueries({ queryKey: SUPPLIERS_STATS_KEY });
        onSuccess?.(body, file as any);
      } else {
        throw new Error(body?.message || 'Import failed');
      }
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Import failed');
      onError?.(err as Error);
    } finally {
      setIsExcelBusy(false);
      setUploadPct(null);
    }
  };

  const handleApprove = async (supplier: SupplierApi) => {
    try {
      await approveSupplier(supplier.id);
      message.success(`${supplier.supplier_name} approved and marked Qualified`);
      await queryClient.invalidateQueries({ queryKey: SUPPLIERS_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: SUPPLIERS_STATS_KEY });
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : 'Approval failed');
    }
  };

  const handleEdit = (supplier: SupplierApi) => {
    navigate(`${suppliersListPath}/${supplier.id}/edit`);
  };

  const handleDelete = (supplier: SupplierApi) => {
    Modal.confirm({
      title: 'Delete supplier',
      content: `Delete "${supplier.supplier_name}"?`,
      okText: 'Delete',
      okType: 'danger',
      async onOk() {
        await api.delete(`/procurement/suppliers/${supplier.id}`);
        message.success('Supplier deleted');
        await queryClient.invalidateQueries({ queryKey: SUPPLIERS_QUERY_KEY });
        await queryClient.invalidateQueries({ queryKey: SUPPLIERS_STATS_KEY });
      },
    });
  };

  const handleAdd = () => {
    navigate(`${suppliersListPath}/new`);
  };

  const totalSuppliers = statistics?.totalSuppliers ?? suppliersRaw.length;
  const activeSuppliers = statistics?.activeSuppliers ?? suppliersRaw.filter((s) => s.is_active).length;
  const approvedSuppliers = statistics?.approvedSuppliers ?? 0;
  const pendingApproval = Math.max(0, totalSuppliers - approvedSuppliers);

  if (isError) {
    return (
      <div>
        <Title level={2}>Suppliers</Title>
        <Card>
          <Text type="danger">Failed to load suppliers. {(error as Error)?.message}</Text>
          <Button type="primary" onClick={() => refetch()} style={{ marginTop: 8 }}>
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Suppliers</Title>
        <Text type="secondary">Manage supplier master data, Excel import/export, and ratings</Text>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total suppliers"
              value={totalSuppliers}
              prefix={<ShopOutlined />}
              valueStyle={{ color: '#145A54' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Active" value={activeSuppliers} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Approved" value={approvedSuppliers} valueStyle={{ color: '#145A54' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Pending approval" value={pendingApproval} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
      </Row>

      <Card>
        {uploadPct !== null && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">Uploading import file…</Text>
            <Progress percent={uploadPct} size="small" />
          </div>
        )}
        <div
          style={{
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '8px',
          }}
        >
          <Space wrap>
            <Search
              placeholder="Search code, name, email…"
              allowClear
              style={{ width: 300 }}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate} disabled={isExcelBusy}>
              Template
            </Button>
            <Button icon={<DownloadOutlined />} onClick={handleExportExcel} disabled={isExcelBusy}>
              Export
            </Button>
            <Upload
              accept=".xlsx,.xls"
              showUploadList={false}
              customRequest={handleImportExcel}
              disabled={isExcelBusy}
            >
              <Button icon={<UploadOutlined />} loading={isExcelBusy}>
                Import
              </Button>
            </Upload>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add supplier
          </Button>
        </div>

        <Spin spinning={isLoading}>
          <Table
            columns={columns}
            dataSource={suppliersRaw}
            rowKey="id"
            pagination={{
              current: supplierPage,
              pageSize: supplierPageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total) => `Total ${total} suppliers`,
              onChange: (page, size) => {
                setSupplierPage(page);
                setSupplierPageSize(size);
              },
            }}
            size="small"
          />
        </Spin>
      </Card>
    </div>
  );
};
